import { resolveImageUrl } from '../../../utils/image';
import type { Category, ProductDraft } from './catalog.types';

type Props = {
  editingProduct: ProductDraft;
  categories: Category[];
  composition: Array<{ name: string; quantity: number }>;
  emptyCompositionRow: { name: string; quantity: number };
  newCategoryNameForProduct: string;
  uploadingImage: boolean;
  productSaving: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  onSubmit: (e: React.FormEvent) => void;
  onUploadImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setEditingProduct: (draft: ProductDraft) => void;
  setNewCategoryNameForProduct: (value: string) => void;
  onCancel: () => void;
};

export default function ProductForm(props: Props) {
  const {
    editingProduct,
    categories,
    composition,
    emptyCompositionRow,
    newCategoryNameForProduct,
    uploadingImage,
    productSaving,
    canCreate,
    canUpdate,
    onSubmit,
    onUploadImage,
    setEditingProduct,
    setNewCategoryNameForProduct,
    onCancel,
  } = props;

  return (
    <form onSubmit={onSubmit} className="admin-card admin-editor-form" style={{ marginBottom: '20px' }}>
      <label className="admin-field">
        <span className="admin-field-label">Название товара</span>
        <input
          className="admin-input"
          data-testid="admin-catalog-product-name-input"
          placeholder="Например: Розовый букет"
          value={editingProduct.name || ''}
          onChange={(e) => setEditingProduct({ ...editingProduct, name: e.target.value })}
          required
        />
      </label>
      <label className="admin-field">
        <span className="admin-field-label">Описание товара</span>
        <textarea className="admin-input" placeholder="Что входит в букет, кому подойдет и т.д." value={editingProduct.description || ''} onChange={(e) => setEditingProduct({ ...editingProduct, description: e.target.value })} />
      </label>
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <label className="admin-field" style={{ flex: 1, minWidth: 160 }}>
          <span className="admin-field-label">Текущая цена, ₽</span>
          <input className="admin-input" type="number" value={editingProduct.price || ''} onChange={(e) => setEditingProduct({ ...editingProduct, price: parseFloat(e.target.value) })} required />
        </label>
        <label className="admin-field" style={{ flex: 1, minWidth: 160 }}>
          <span className="admin-field-label">Старая цена, ₽ (опционально)</span>
          <input className="admin-input" type="number" value={editingProduct.old_price || ''} onChange={(e) => setEditingProduct({ ...editingProduct, old_price: parseFloat(e.target.value) })} />
        </label>
        <label className="admin-field" style={{ flex: 1, minWidth: 160 }}>
          <span className="admin-field-label">Себестоимость, ₽</span>
          <input
            className="admin-input"
            type="number"
            min={0}
            value={editingProduct.cost_price ?? ''}
            onChange={(e) => setEditingProduct({ ...editingProduct, cost_price: e.target.value ? parseFloat(e.target.value) : undefined })}
            placeholder="Опционально"
          />
        </label>
      </div>
      <label className="admin-field" style={{ flex: 1, minWidth: 220 }}>
        <span className="admin-field-label">Категория товара</span>
        <select
          className="admin-input"
          value={editingProduct.category_id ?? ''}
          onChange={(e) => {
            const nextValue = e.target.value;
            setEditingProduct({
              ...editingProduct,
              category_id: nextValue === '__new__' || nextValue === 'all' ? nextValue : nextValue ? parseInt(nextValue, 10) : undefined,
            });
          }}
        >
          <option value="all">Общая (все товары)</option>
          <option value="">Без категории</option>
          <option value="__new__">Новая категория...</option>
          {categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </select>
      </label>
      {editingProduct.category_id === '__new__' && (
        <div className="admin-quick-category">
          <label className="admin-field">
            <span className="admin-field-label">Название новой категории</span>
            <input className="admin-input" placeholder="Например: Монобукеты" value={newCategoryNameForProduct} onChange={(e) => setNewCategoryNameForProduct(e.target.value)} />
          </label>
        </div>
      )}
      <div className="admin-field">
        <span className="admin-field-label">Изображения товара</span>
        <div className="admin-upload-row">
          <label className="admin-btn admin-btn-secondary" style={{ cursor: uploadingImage ? 'not-allowed' : 'pointer' }}>
            {uploadingImage ? 'Загрузка...' : 'Загрузить изображения'}
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple onChange={onUploadImage} disabled={uploadingImage} style={{ display: 'none' }} />
          </label>
        </div>
        {(editingProduct.images || []).length > 0 && (
          <div className="admin-upload-preview-list">
            {(editingProduct.images || []).map((img, idx) => (
              <div className="admin-upload-preview-item" key={`${img}-${idx}`}>
                <img src={resolveImageUrl(img)} alt="" />
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  disabled={idx === 0}
                  onClick={() => {
                    const next = [...(editingProduct.images || [])];
                    [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
                    setEditingProduct({ ...editingProduct, images: next });
                  }}
                >
                  ↑
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  disabled={idx === (editingProduct.images || []).length - 1}
                  onClick={() => {
                    const next = [...(editingProduct.images || [])];
                    [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
                    setEditingProduct({ ...editingProduct, images: next });
                  }}
                >
                  ↓
                </button>
                <button
                  type="button"
                  className="admin-btn admin-btn-secondary admin-btn-sm"
                  onClick={() => setEditingProduct({ ...editingProduct, images: (editingProduct.images || []).filter((_, imageIndex) => imageIndex !== idx) })}
                >
                  Удалить
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="admin-field">
        <span className="admin-field-label">Состав (название - количество)</span>
        <div className="admin-composition-grid">
          {composition.map((item, index) => (
            <div className="admin-composition-row" key={`composition-row-${index}`}>
              <input
                className="admin-input"
                placeholder="Название цветка/элемента"
                value={item.name}
                onChange={(e) => {
                  const next = [...composition];
                  next[index] = { ...next[index], name: e.target.value };
                  setEditingProduct({ ...editingProduct, composition: next });
                }}
              />
              <input
                className="admin-input"
                type="number"
                min={1}
                placeholder="Кол-во"
                value={item.quantity}
                onChange={(e) => {
                  const next = [...composition];
                  next[index] = { ...next[index], quantity: Math.max(1, parseInt(e.target.value || '1', 10)) };
                  setEditingProduct({ ...editingProduct, composition: next });
                }}
              />
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-sm"
                onClick={() => {
                  const next = composition.filter((_, rowIndex) => rowIndex !== index);
                  setEditingProduct({ ...editingProduct, composition: next.length ? next : [emptyCompositionRow] });
                }}
              >
                Удалить
              </button>
            </div>
          ))}
          <button type="button" className="admin-btn admin-btn-secondary" onClick={() => setEditingProduct({ ...editingProduct, composition: [...composition, { ...emptyCompositionRow }] })}>
            + Добавить строку состава
          </button>
        </div>
      </div>
      <label className="admin-field">
        <span className="admin-field-label">Количество в наличии (шт.)</span>
        <input
          className="admin-input"
          type="number"
          min={0}
          value={editingProduct.stock_quantity ?? ''}
          onChange={(e) => setEditingProduct({ ...editingProduct, stock_quantity: e.target.value ? parseInt(e.target.value, 10) : undefined })}
          placeholder="Например: 100"
          disabled={editingProduct.stock_quantity == null}
        />
      </label>
      <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <input
          type="checkbox"
          checked={editingProduct.stock_quantity == null}
          onChange={(e) =>
            setEditingProduct({
              ...editingProduct,
              stock_quantity: e.target.checked ? undefined : 0,
            })
          }
        />
        Неограниченное количество
      </label>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input type="checkbox" checked={editingProduct.in_stock ?? true} onChange={(e) => setEditingProduct({ ...editingProduct, in_stock: e.target.checked })} />
          В наличии
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input type="checkbox" checked={editingProduct.is_hit ?? false} onChange={(e) => setEditingProduct({ ...editingProduct, is_hit: e.target.checked })} />
          Хит продаж
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <input type="checkbox" checked={editingProduct.is_active ?? true} onChange={(e) => setEditingProduct({ ...editingProduct, is_active: e.target.checked })} />
          Активен
        </label>
      </div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
        <button type="submit" className="admin-btn admin-btn-primary" disabled={productSaving || uploadingImage || (editingProduct?.id ? !canUpdate : !canCreate)}>
          {productSaving ? 'Сохранение...' : 'Сохранить'}
        </button>
        <button type="button" className="admin-btn admin-btn-secondary" disabled={productSaving || uploadingImage} onClick={onCancel}>
          Отмена
        </button>
      </div>
    </form>
  );
}

